import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../../redux/hooks';
import { closeModal } from '../../../../redux/slices/modal';
import { PrimaryButton, SecondaryButton } from '../../../Button';
import { FieldLabel, InputField, TextAreaField } from '../../../Form';
import KubitModal from '../../../KubitModal';
import {
  CHART_NAME_MAX_LENGTH,
  DESCRIPTION_MAX_LENGTH,
} from '../../../../config/constants';
import toastMessage from '../../../../utils/toastMessage';
import { useMutation } from '@apollo/client';
import { ANALYSIS_COPY } from '../../../../utils/apollo/analysis';
import {
  IAnalysisCopyMutation,
  IAnalysisCopyMutationVariables,
} from '../../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { assertNotNil } from '../../../../utils/types';
import useExecutedReport from '../../../../utils/builder/useExecutedReport';
import { usePrepareChartPrefs } from '../../hooks/useAnalysisSetChartPref';
import { clearReplaceReport } from '../../../../redux/slices/cachedEntities';
import { useChartContext } from '../../ChartContext';
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: 10px 0;
`;

const FooterActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

interface IProps {
  analysisId: string;
}

const DuplicateReportModal = ({ analysisId }: IProps) => {
  const dispatch = useAppDispatch();
  const {
    metadata: { display, description, query },
  } = useChartContext();

  const preparePrefInput = usePrepareChartPrefs(analysisId);

  const handleExecutedReport = useExecutedReport();

  const { control, handleSubmit } = useForm({
    defaultValues: {
      display: `${display} 1`,
      description,
    },
  });

  const [copyAnalysis] = useMutation<
    IAnalysisCopyMutation,
    IAnalysisCopyMutationVariables
  >(ANALYSIS_COPY);

  const onCloseModal = () => {
    dispatch(closeModal());
  };

  const onSubmit = async (formData: {
    display: string;
    description: string;
  }) => {
    try {
      const { pref } = preparePrefInput();
      const resp = await copyAnalysis({
        variables: {
          input: {
            schemaId: query.schema.id,
            analysisId,
            display: formData.display,
            description: formData.description,
            pref,
          },
        },
      });

      const errors = resp.data?.analysisCopy?.errors;

      if (printGraphqlErrors(errors)) {
        return;
      }

      const entity = resp.data?.analysisCopy?.entity;
      assertNotNil(entity);

      handleExecutedReport(entity);

      toastMessage({
        message: 'Report has been successfully duplicated.',
        type: 'info',
      });

      // KBT-1092
      dispatch(clearReplaceReport());
    } catch (error) {
      console.error('Failed to save as new:', error);
      toastMessage({
        message: 'Failed to duplicate report',
        type: 'error',
      });
    }

    onCloseModal();
  };

  return (
    <KubitModal open onClose={onCloseModal} header="Duplicate report">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container>
          <FieldLabel>Name</FieldLabel>
          <Controller
            name="display"
            control={control}
            render={({ field }) => (
              <InputField {...field} maxLength={CHART_NAME_MAX_LENGTH} />
            )}
          />
          <FieldLabel>Description</FieldLabel>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextAreaField
                {...field}
                minRows={3}
                maxLength={DESCRIPTION_MAX_LENGTH}
              />
            )}
          />
        </Container>
        <Footer>
          <div />
          <FooterActions>
            <SecondaryButton
              type="button"
              onClick={onCloseModal}
              trackContext="reports"
              trackAction="duplicate - cancel"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              trackContext="reports"
              trackAction="duplicate - save"
            >
              Save
            </PrimaryButton>
          </FooterActions>
        </Footer>
      </form>
    </KubitModal>
  );
};

export default DuplicateReportModal;
